/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssLabelComponent } from '../label/label.component';
import { UmssRadioComponent } from '../radio/radio.component';

@Component({
  selector: 'umss-rich-radio-group',
  standalone: true,
  imports: [CommonModule, UmssRadioComponent, UmssLabelComponent],
  templateUrl: './rich-radio-group.component.html',
  styleUrls: ['./rich-radio-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssRichRadioGroupComponent),
      multi: true
    }
  ]
})
export class UmssRichRadioGroupComponent implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  /** Content props */
  @Input() label: string = 'Label';
  @Input() secondaryText: string = 'Secondary text';
  @Input() value: unknown;
  @Input() name?: string;
  @Input() ariaLabel?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;

  /** Value prop */
  private _checked: boolean = false;
  @Input() set checked(val: boolean) {
    this._checked = val;
    if (val) {
      this._onChange(this.value);
    } else {
      this._onChange(null);
    }
  }
  get checked(): boolean {
    return this._checked;
  }

  /** Event outputs */
  @Output() checkedChange = new EventEmitter<unknown>();
  @Output() change = new EventEmitter<unknown>();

  /** ControlValueAccessor implementation */
  private _onChange: (value: unknown) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: unknown): void {
    this._checked = value === this.value;
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onRadioChange(selectedValue: unknown): void {
    if (this.disabled) return;

    this._checked = true;
    this._onChange(selectedValue);
    this._onTouched();
    this.checkedChange.emit(selectedValue);
    this.change.emit(selectedValue);
  }

  /** Computed properties */
  get computedAriaLabel(): string {
    return this.ariaLabel || this.label || 'Radio option';
  }
}
